import React, { useState, useRef } from 'react';
import { useApp } from '@/context/AppContext';
import { TopBar } from '@/components/TopBar';
import { Page } from '@/components/Page';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import api, { errMsg } from '@/lib/api';
import { toast } from 'sonner';
import { money } from '@/lib/helpers';
import { Plus, Pencil, Trash2, Check, Upload, Download, Loader2, Users, Receipt } from 'lucide-react';

const money0 = (n) => `\u20b9${Number(n || 0).toLocaleString('en-IN')}`;

function StaffRow({ s, stores, onSave, onDelete, busy }) {
  const [f, setF] = useState({ name: s.name || '', phone: s.phone || '', role: s.role || '', monthly_target: s.monthly_target ?? '', commission_pct: s.commission_pct ?? '' });
  const dirty = f.name !== (s.name || '') || f.phone !== (s.phone || '') || f.role !== (s.role || '')
    || String(f.monthly_target) !== String(s.monthly_target ?? '') || String(f.commission_pct) !== String(s.commission_pct ?? '');
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  return (
    <div className="grid grid-cols-12 items-center gap-1.5 rounded-lg border border-border p-1.5" data-testid={`staff-row-${s.id}`}>
      <Input className="col-span-3 h-9 text-xs" value={f.name} onChange={(e) => set('name', e.target.value)} placeholder="Name" data-testid={`staff-name-${s.id}`} />
      <Input className="col-span-2 h-9 text-xs" value={f.phone} onChange={(e) => set('phone', e.target.value)} placeholder="Phone" />
      <Input className="col-span-2 h-9 text-xs" value={f.role} onChange={(e) => set('role', e.target.value)} placeholder="Role" />
      <Input className="col-span-2 h-9 text-xs" type="number" value={f.monthly_target} onChange={(e) => set('monthly_target', e.target.value)} placeholder="Target" />
      <Input className="col-span-1 h-9 text-xs" type="number" value={f.commission_pct} onChange={(e) => set('commission_pct', e.target.value)} placeholder="%" />
      <div className="col-span-2 flex items-center justify-end gap-1">
        <button disabled={!dirty || busy} onClick={() => onSave(s, { ...f, monthly_target: Number(f.monthly_target) || 0, commission_pct: Number(f.commission_pct) || 0 })} data-testid={`staff-save-${s.id}`} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-primary disabled:opacity-40"><Check className="h-4 w-4" /></button>
        <button disabled={busy} onClick={() => onDelete(s.id)} data-testid={`staff-del-${s.id}`} className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

export default function StaffPage() {
  const { stores, currentStore } = useApp();
  const qc = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [nw, setNw] = useState({ name: '', phone: '', role: '', monthly_target: '', commission_pct: '' });
  const fileRef = useRef(null);
  const [imp, setImp] = useState(null); // import summary
  const blankLog = { staff: '', date: new Date().toISOString().slice(0, 10), store_id: currentStore || '', bill_no: '', sale_amount: '', incentive_pct: '' };
  const [logForm, setLogForm] = useState(blankLog);
  const [editLog, setEditLog] = useState(null);
  const [delLog, setDelLog] = useState(null);

  const { data: staff = [] } = useQuery({ queryKey: ['staff'], queryFn: async () => (await api.get('/incentives/staff')).data.data });
  const { data: logs = [] } = useQuery({ queryKey: ['staff-logs'], queryFn: async () => (await api.get('/incentives/logs')).data.data });

  const refStaff = () => qc.invalidateQueries({ queryKey: ['staff'] });
  const refLogs = () => qc.invalidateQueries({ queryKey: ['staff-logs'] });
  const storeName = (id) => stores.find((s) => s.id === id)?.name || 'All / Common';

  // ---- staff ----
  const addStaff = async () => {
    if (!nw.name.trim()) { toast.error('Enter a staff name'); return; }
    setBusy(true);
    try { await api.post('/incentives/staff', { ...nw, monthly_target: Number(nw.monthly_target) || 0, commission_pct: Number(nw.commission_pct) || 0 }); setNw({ name: '', phone: '', role: '', monthly_target: '', commission_pct: '' }); toast.success('Staff added'); refStaff(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const saveStaff = async (s, patch) => { setBusy(true); try { await api.put(`/incentives/staff/${s.id}`, patch); toast.success('Saved'); refStaff(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };
  const delStaff = async (id) => { setBusy(true); try { await api.delete(`/incentives/staff/${id}`); toast.success('Removed'); refStaff(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };

  // ---- incentives: quick add ----
  const addLog = async () => {
    if (!logForm.staff) { toast.error('Choose a staff member'); return; }
    if (!logForm.sale_amount) { toast.error('Enter a sale amount'); return; }
    setBusy(true);
    try {
      await api.post('/incentives/logs', { ...logForm, sale_amount: Number(logForm.sale_amount), incentive_pct: Number(logForm.incentive_pct) || 0 });
      toast.success('Incentive added'); setLogForm({ ...blankLog, staff: logForm.staff }); refLogs();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const saveLog = async () => {
    setBusy(true);
    try {
      await api.put(`/incentives/logs/${editLog.id}`, { sale_amount: Number(editLog.sale_amount) || 0, incentive_pct: Number(editLog.incentive_pct) || 0, date: editLog.date, staff: editLog.staff, store_id: editLog.store_id, bill_no: editLog.bill_no });
      toast.success('Updated'); setEditLog(null); refLogs();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const doDelLog = async () => { setBusy(true); try { await api.delete(`/incentives/logs/${delLog}`); toast.success('Deleted'); setDelLog(null); refLogs(); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); } };

  // ---- incentives: excel import ----
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true); setImp(null);
    try {
      const fd = new FormData(); fd.append('file', file); fd.append('default_store_id', currentStore || '');
      const r = await api.post('/incentives/logs/import/validate', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setImp(r.data.data);
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  };
  const commitImport = async () => {
    if (!imp) return;
    setBusy(true);
    try {
      const r = await api.post('/incentives/logs/import/commit', { rows: imp.rows, filename: imp.filename, batch_key: imp.batch_key });
      toast.success(`Imported ${r.data.data.added} incentive(s)`); setImp(null); refLogs();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const downloadTemplate = async () => {
    try {
      const r = await api.get('/incentives/logs/template', { responseType: 'blob' });
      const url = URL.createObjectURL(r.data); const a = document.createElement('a');
      a.href = url; a.download = 'Staff Incentives Import Template.xlsx'; a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error('Download failed'); }
  };

  const totalInc = logs.reduce((s, l) => s + (l.incentive || 0), 0);

  return (
    <Page>
      <TopBar title="Staff & Incentives" showStore={false} />
      <div className="space-y-4 px-4 pt-4">
        {/* Staff management */}
        <Card data-testid="staff-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Users className="h-5 w-5" />Staff</CardTitle>
            <CardDescription>Add the people who make sales (no login needed). Name is required; phone, role, monthly target and commission % are optional. They appear as the Salesperson in Billing and in incentives below.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="hidden grid-cols-12 gap-1.5 px-1.5 text-[10px] font-medium uppercase text-muted-foreground sm:grid">
              <span className="col-span-3">Name</span><span className="col-span-2">Phone</span><span className="col-span-2">Role</span><span className="col-span-2">Target</span><span className="col-span-1">%</span><span className="col-span-2 text-right">Actions</span>
            </div>
            {staff.length === 0 ? <p className="py-3 text-center text-sm text-muted-foreground">No staff yet. Add your first below.</p>
              : staff.map((s) => <StaffRow key={s.id} s={s} stores={stores} onSave={saveStaff} onDelete={delStaff} busy={busy} />)}
            <div className="grid grid-cols-12 items-center gap-1.5 rounded-lg border border-dashed border-border p-1.5">
              <Input className="col-span-3 h-9 text-xs" value={nw.name} onChange={(e) => setNw({ ...nw, name: e.target.value })} placeholder="New staff name" data-testid="staff-new-name" onKeyDown={(e) => { if (e.key === 'Enter') addStaff(); }} />
              <Input className="col-span-2 h-9 text-xs" value={nw.phone} onChange={(e) => setNw({ ...nw, phone: e.target.value })} placeholder="Phone" />
              <Input className="col-span-2 h-9 text-xs" value={nw.role} onChange={(e) => setNw({ ...nw, role: e.target.value })} placeholder="Role" />
              <Input className="col-span-2 h-9 text-xs" type="number" value={nw.monthly_target} onChange={(e) => setNw({ ...nw, monthly_target: e.target.value })} placeholder="Target" />
              <Input className="col-span-1 h-9 text-xs" type="number" value={nw.commission_pct} onChange={(e) => setNw({ ...nw, commission_pct: e.target.value })} placeholder="%" />
              <div className="col-span-2 flex justify-end">
                <button onClick={addStaff} disabled={busy} data-testid="staff-add" className="flex items-center gap-1 rounded-md bg-primary px-2 py-2 text-xs font-semibold text-primary-foreground hover:opacity-90 disabled:opacity-50"><Plus className="h-4 w-4" />Add</button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Incentives */}
        <Card data-testid="incentives-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2"><Receipt className="h-5 w-5" />Staff Incentives</CardTitle>
            <CardDescription>Add incentives one by one, or import many from Excel. Total incentive so far: <b>{money0(totalInc)}</b>.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* quick add */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-6">
              <div className="col-span-2 sm:col-span-1"><Label className="text-xs">Staff</Label>
                <Select value={logForm.staff} onValueChange={(v) => setLogForm({ ...logForm, staff: v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs" data-testid="log-staff"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Date</Label><Input type="date" className="mt-1 h-9 text-xs" value={logForm.date} onChange={(e) => setLogForm({ ...logForm, date: e.target.value })} /></div>
              <div><Label className="text-xs">Store</Label>
                <Select value={logForm.store_id || 'none'} onValueChange={(v) => setLogForm({ ...logForm, store_id: v === 'none' ? '' : v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue placeholder="Common" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Common</SelectItem>{stores.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Bill no</Label><Input className="mt-1 h-9 text-xs" value={logForm.bill_no} onChange={(e) => setLogForm({ ...logForm, bill_no: e.target.value })} /></div>
              <div><Label className="text-xs">Sale amount</Label><Input type="number" className="mt-1 h-9 text-xs" value={logForm.sale_amount} onChange={(e) => setLogForm({ ...logForm, sale_amount: e.target.value })} data-testid="log-amount" /></div>
              <div className="flex items-end gap-1">
                <div className="flex-1"><Label className="text-xs">Inc %</Label><Input type="number" className="mt-1 h-9 text-xs" value={logForm.incentive_pct} onChange={(e) => setLogForm({ ...logForm, incentive_pct: e.target.value })} /></div>
                <Button className="h-9" onClick={addLog} disabled={busy} data-testid="log-add"><Plus className="h-4 w-4" /></Button>
              </div>
            </div>

            {/* import */}
            <div className="flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border p-2">
              <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={onFile} data-testid="inc-import-file" />
              <Button size="sm" variant="secondary" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="inc-import-choose"><Upload className="mr-2 h-4 w-4" />Import Excel</Button>
              <Button size="sm" variant="outline" onClick={downloadTemplate}><Download className="mr-2 h-4 w-4" />Template</Button>
              <span className="text-xs text-muted-foreground">Columns: date, staff, store, bill_no, sale_amount, incentive_pct</span>
              {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            </div>
            {imp && (
              <div className="rounded-lg border border-sky-200 bg-sky-50 p-3 text-sm text-sky-800" data-testid="inc-import-summary">
                <p><b>{imp.to_import}</b> valid, <b>{imp.invalid_rows}</b> skipped · Total sales {money0(imp.totals?.sale_amount)} · Total incentive {money0(imp.totals?.incentive)}</p>
                {imp.row_errors?.length > 0 && <p className="mt-1 text-xs text-destructive">{imp.row_errors.length} row(s) have problems and will be skipped.</p>}
                <div className="mt-2 flex gap-2">
                  <Button size="sm" onClick={commitImport} disabled={busy || imp.to_import < 1} data-testid="inc-import-commit">Import {imp.to_import}</Button>
                  <Button size="sm" variant="ghost" onClick={() => setImp(null)}>Cancel</Button>
                </div>
              </div>
            )}

            {/* recent logs */}
            <div className="-mx-4 overflow-x-auto px-4">
              <table className="min-w-[640px] w-full text-sm [&_td]:whitespace-nowrap [&_td]:px-2 [&_th]:whitespace-nowrap [&_th]:px-2" data-testid="inc-log-table">
                <thead className="text-left text-xs text-muted-foreground"><tr>
                  <th className="py-1">Date</th><th>Staff</th><th>Store</th><th>Bill</th><th>Sale</th><th>%</th><th>Incentive</th><th></th>
                </tr></thead>
                <tbody>
                  {logs.length === 0 ? <tr><td colSpan={8} className="py-3 text-center text-muted-foreground">No incentives yet.</td></tr>
                    : logs.slice(0, 200).map((l) => (
                      <tr key={l.id} className="border-t" data-testid={`inc-log-${l.id}`}>
                        <td className="py-1">{l.date}</td>
                        <td className="py-1 font-medium">{l.staff}</td>
                        <td className="py-1 text-muted-foreground">{storeName(l.store_id)}</td>
                        <td className="py-1">{l.bill_no || '\u2014'}</td>
                        <td className="py-1">{money(l.sale_amount)}</td>
                        <td className="py-1">{l.incentive_pct}%</td>
                        <td className="py-1 font-semibold">{money(l.incentive)}</td>
                        <td className="py-1">
                          <div className="flex justify-end gap-1">
                            <button onClick={() => setEditLog({ ...l })} data-testid={`inc-log-edit-${l.id}`} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-primary"><Pencil className="h-4 w-4" /></button>
                            <button onClick={() => setDelLog(l.id)} data-testid={`inc-log-del-${l.id}`} className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-destructive"><Trash2 className="h-4 w-4" /></button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* edit log dialog */}
      <AlertDialog open={!!editLog} onOpenChange={(o) => { if (!o) setEditLog(null); }}>
        <AlertDialogContent data-testid="inc-log-edit-dialog">
          <AlertDialogHeader><AlertDialogTitle>Edit incentive</AlertDialogTitle>
            <AlertDialogDescription>Update this incentive entry.</AlertDialogDescription></AlertDialogHeader>
          {editLog && (
            <div className="grid grid-cols-2 gap-2">
              <div className="col-span-2"><Label className="text-xs">Staff</Label>
                <Select value={editLog.staff} onValueChange={(v) => setEditLog({ ...editLog, staff: v })}>
                  <SelectTrigger className="mt-1 h-9 text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>{staff.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs">Date</Label><Input type="date" className="mt-1 h-9 text-xs" value={editLog.date} onChange={(e) => setEditLog({ ...editLog, date: e.target.value })} /></div>
              <div><Label className="text-xs">Bill no</Label><Input className="mt-1 h-9 text-xs" value={editLog.bill_no || ''} onChange={(e) => setEditLog({ ...editLog, bill_no: e.target.value })} /></div>
              <div><Label className="text-xs">Sale amount</Label><Input type="number" className="mt-1 h-9 text-xs" value={editLog.sale_amount} onChange={(e) => setEditLog({ ...editLog, sale_amount: e.target.value })} data-testid="inc-log-edit-amount" /></div>
              <div><Label className="text-xs">Inc %</Label><Input type="number" className="mt-1 h-9 text-xs" value={editLog.incentive_pct} onChange={(e) => setEditLog({ ...editLog, incentive_pct: e.target.value })} /></div>
            </div>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={saveLog} disabled={busy} data-testid="inc-log-edit-save">Save</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* delete log confirm */}
      <AlertDialog open={!!delLog} onOpenChange={(o) => { if (!o) setDelLog(null); }}>
        <AlertDialogContent data-testid="inc-log-delete-dialog">
          <AlertDialogHeader><AlertDialogTitle>Delete this incentive?</AlertDialogTitle>
            <AlertDialogDescription>This permanently removes the incentive entry.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={doDelLog} disabled={busy} data-testid="inc-log-delete-confirm" className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Page>
  );
}
